import { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { TextInput, SelectInput } from '~/shared/components/form/Inputs';
import { identityOptions } from '~/shared/config';
import { Button, Col, Row, Form, FormGroup, Input, Label } from 'reactstrap';
import { agreementClauseOptions } from '~/shared/agreements';
import {
    setAgreementClauses,
    setAgreementTermPublicContent,
    subscribeToBlog,
} from '~/services/registration';
import { Loading } from '~/shared/components/Loading';
import moment from 'moment';
import { fetchAgreement } from '~/services/agreement';
import { CampaignContext } from '~/context/campaign';
import toast from 'react-hot-toast';

interface ClauseOption {
    id?: number;
    name: string;
    label: string;
    agree: boolean;
}

export const Agreements = ({ }: any) => {
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [existingAgreement, setExistingAgreement] = useState<any>(null);
    const [clauseOptions, setClauseOptions] = useState<ClauseOption[]>([]);
    const [optionalClause, setOptionalClause] = useState<ClauseOption | null>(null);
    const history = useHistory();
    const today = moment().format('Y-MM-DD');
    const {
        register,
        handleSubmit,
        watch,
        control,
        reset,
        setValue,
        getValues,
        formState: { errors, isSubmitting, isValid },
    } = useForm({
        defaultValues: {
            ag_certify_identity: '' as string | number,
            ag_signature_first_name: '',
            ag_signature_last_name: '',
            ag_signature_date: '',
            ag_understand_hhl_may_use_public_content: false,
            subscribe_to_blog: false,
        }
    });

    const getLegacyAgreement = async (campaign_id: number) => {
        const agreement = await fetchAgreement(campaign_id);

        if (agreement.detail === 'Not found.') {
            setExistingAgreement(false);
            // At this point, neither application agreement terms nor legacy agreement exists
            // we can instantiate the form with new agreement terms
            const opts = agreementClauseOptions.map((opt) => {
                return {
                    name: opt.fieldname,
                    label: opt.label,
                    agree: false,
                };
            });
            const requiredOpts = opts.filter(
                (x) => x.name !== 'ag_understand_hhl_may_use_public_content'
            );
            const optionalOpts = opts.filter(
                (x) => x.name === 'ag_understand_hhl_may_use_public_content'
            );

            setClauseOptions(requiredOpts);
            setOptionalClause(optionalOpts[0]);
            setValue('ag_signature_date', today);
        } else {
            // At this point, legacy agreement exists
            const legacySigned = agreement.signature_date;
            const { clauses } = agreement;

            // If clauses are empty (AgreementClause rows were never created),
            // fall back to shared agreementClauseOptions config
            if (clauses.length === 0) {
                const opts = agreementClauseOptions.map((opt) => {
                    return {
                        name: opt.fieldname,
                        label: opt.label,
                        agree: !!legacySigned,
                    };
                });
                const requiredOpts = opts.filter(
                    (x) => x.name !== 'ag_understand_hhl_may_use_public_content'
                );
                const optionalOpts = opts.filter(
                    (x) => x.name === 'ag_understand_hhl_may_use_public_content'
                );
                setClauseOptions(requiredOpts);
                setOptionalClause(optionalOpts[0]);
            } else {
                const opts = clauses.map((clause) => {
                    return {
                        id: clause.id,
                        name: clause.name,
                        label: clause.template,
                        agree: !!legacySigned,
                    };
                });
                setClauseOptions(opts);
            }

            const ag_certify_identity = identityOptions.find(
                (x) => x.value === Number(agreement.identity)
            );

            // Split signature_name into first and last name
            const nameParts = (agreement.signature_name || '').trim().split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            const resetValues = {
                ag_signature_first_name: firstName,
                ag_signature_last_name: lastName,
                ag_signature_date: agreement.signature_date,
                ag_certify_identity: ag_certify_identity?.value || '',
                ag_understand_hhl_may_use_public_content: agreement.ag_understand_hhl_may_use_public_content || false,
            };

            reset(resetValues);
            setExistingAgreement(agreement);
        }

        setLoading(false);
    };

    useEffect(() => {
        // Determine if new agreements are saved
        if (!campaign) {
            return;
        }
        const { campaignApplication, campaignDetail } = campaign;
        const signatureDate = campaignApplication?.ag_signature_date;
        const signature = campaignApplication?.ag_signature;
        const identity = campaignApplication?.ag_certify_identity;
        const ag_understand_hhl_may_use_public_content =
            campaignApplication?.ag_understand_hhl_may_use_public_content;

        if (!signatureDate) {
            // Check for legacy agreement
            getLegacyAgreement(campaignDetail?.campaign_id);
        } else {
            // Agreement exists via Application
            console.log('🔍 Application Agreement Debug:', {
                'identity': identity,
                'identityOptions': identityOptions,
                'full campaignApplication': campaignApplication
            });

            const ag_certify_identity = identityOptions.find(
                (x) => x.value === Number(identity)
            );

            console.log('🎯 Found ag_certify_identity:', ag_certify_identity);

            setExistingAgreement(true);
            const opts = agreementClauseOptions.map((opt) => {
                return {
                    name: opt.fieldname,
                    label: opt.label,
                    agree: true,
                };
            });

            const requiredOpts = opts.filter(
                (x) => x.name !== 'ag_understand_hhl_may_use_public_content'
            );
            const optionalOpts = opts.filter(
                (x) => x.name === 'ag_understand_hhl_may_use_public_content'
            );

            setClauseOptions(requiredOpts);
            setOptionalClause(optionalOpts[0]);

            // Split signature into first and last name
            const nameParts = (signature || '').trim().split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            const resetValues = {
                ag_signature_first_name: firstName,
                ag_signature_last_name: lastName,
                ag_signature_date: signatureDate,
                ag_certify_identity: ag_certify_identity?.value || '',
                ag_understand_hhl_may_use_public_content,
            };

            console.log('🔄 Resetting form with values:', resetValues);
            reset(resetValues);
            setLoading(false);
        }
    }, [campaign]);

    // Debug: Log current form values
    const currentFormValues = watch();
    console.log('📝 Current form values:', currentFormValues);

    const disableAgree = (i: number) => {
        if (i === 0) {
            return false;
        }
        const prevOpt = clauseOptions[i - 1];
        if (prevOpt.agree) {
            return false;
        }
        return true;
    };

    const handleNext = async (data: any) => {
        try {
            toast.loading('Saving agreements...');
            setSaving(true);
            
            // Combine first and last name into ag_signature
            const ag_signature = `${data.ag_signature_first_name} ${data.ag_signature_last_name}`.trim();
            
            let resp;
            if (existingAgreement) {
                // Only update the ag_understand_hhl_may_use_public_content
                resp = await setAgreementTermPublicContent(
                    {
                        ag_understand_hhl_may_use_public_content:
                            data.ag_understand_hhl_may_use_public_content,
                    },
                    campaign.campaignDetail.campaign_id
                );
            } else {
                // const agreement_clause_ids = clauseOptions.map((x) => x.id);
                resp = await setAgreementClauses(
                    {
                        clause_options: clauseOptions,
                        ...data,
                        ag_signature,
                    },
                    campaign.campaignDetail.campaign_id
                );
            }

            toast.dismiss();

            if (resp.detail) {
                if (resp.detail === 'Invalid token.') {
                    localStorage.removeItem('token');
                    history.push('/login');
                    return;
                }
                toast.error(resp.detail);
                setSaving(false);
                return;
            }

            if (existingAgreement) {
                campaignDispatch({
                    type: 'set_application',
                    payload: resp,
                });
            } else {
                campaignDispatch({
                    type: 'set_campaign',
                    payload: resp,
                });
            }
            if (data.subscribe_to_blog) {
                subscribeToBlog({
                    first_name: data.ag_signature_first_name,
                    last_name: data.ag_signature_last_name,
                }).catch((e) => console.error('Blog subscription error:', e));
            }

            toast.success('Agreements saved!');
            history.push('/dashboard');
        } catch (e) {
            toast.dismiss();
            toast.error('There was an error saving your agreements');
            setSaving(false);
            console.error('Error saving agreements:', e);
        }
    };

    const handleCheck = (i: number) => {
        let newOpts = [...clauseOptions];
        newOpts[i]['agree'] = !newOpts[i]['agree'];
        setClauseOptions(newOpts);
    };

    if (loading) {
        return <Loading />;
    }

    const disableNext = clauseOptions.filter((x) => !x.agree).length > 0;

    return (
        <div className='container__inner' style={{ minHeight: '100vh', marginBottom: 0, paddingBottom: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ flexGrow: 1 }}>
                <Row>
                    <Col xl={6}>
                        <h1 className='h2 mb-5'>Agreements</h1>
                    </Col>
                    <Col
                        xl={6}
                        className='d-none d-md-flex justify-content-xl-end align-items-start'
                    >
                        <Button
                            color='outline-dark'
                            disabled={isSubmitting}
                            onClick={() => history.push('/patient/edit')}
                        >
                            Cancel
                        </Button>
                        {!existingAgreement && (
                            <Button
                                color='primary'
                                // onClick={handleNext}
                                type='submit'
                                disabled={saving || disableNext || !isValid}
                            >
                                {saving ? `Saving...` : `Save`}
                            </Button>
                        )}
                    </Col>
                </Row>
                <h2 className='h4 mb-5'>
                    Please review and check all agreements below.
                </h2>
                <Row className='mb-0'>
                <Col lg={9}>
                    <Form onSubmit={handleSubmit(handleNext)}>
                        <Label>
                            I Certify that I understand{' '}
                            <span className='required'>*</span>
                        </Label>
                        {clauseOptions.map((clause, i) => {
                            const disabled =
                                existingAgreement || disableAgree(i);
                            return (
                                <FormGroup check className='mb-4' key={i}>
                                    {/* <h2 className='mb-3'>{i + 1}</h2> */}
                                    {/* <p dangerouslySetInnerHTML={{ __html: clause.label }}></p> */}

                                    <>
                                        <Input
                                            id={`clause-${i}`}
                                            type='checkbox'
                                            onChange={() => handleCheck(i)}
                                            name={`clause-${i}`}
                                            defaultChecked={existingAgreement}
                                            disabled={disabled}
                                        />{' '}
                                        <Label
                                            check
                                            dangerouslySetInnerHTML={{
                                                __html: clause.label,
                                            }}
                                        />
                                        {/* {disabled
                        ? ` You need to accept the prior terms first`
                        : ` I accept`} */}
                                    </>
                                </FormGroup>
                            );
                        })}

                        <hr />

                        <div className='my-4'>
                            <Label>
                                (Optional) I give Help Hope Live Permission:
                            </Label>
                            <FormGroup check>
                                <input
                                    type='checkbox'
                                    className='form-check-input'
                                    {...register(
                                        'ag_understand_hhl_may_use_public_content'
                                    )}
                                />{' '}
                                <Label
                                    check
                                    dangerouslySetInnerHTML={{
                                        __html: optionalClause?.label || '',
                                    }}
                                />
                            </FormGroup>
                        </div>

                        <hr />

                        <SelectInput
                            control={control}
                            required={true}
                            ariaReauired={true}
                            name='ag_certify_identity'
                            label='I certify that I am the:'
                            defaultLabel={null}
                            options={identityOptions}
                            readOnly={!!existingAgreement}
                        />

                        <Label className='mb-3'>Name of individual saving these agreements:</Label>
                        <Row className='align-items-end'>
                            <Col>
                                <TextInput
                                    name='ag_signature_first_name'
                                    label='First Name'
                                    control={control}
                                    required={true}
                                    ariaRequired={true}
                                    readOnly={!!existingAgreement}
                                    maxLength={undefined}
                                    step={undefined}
                                    min={undefined}
                                    max={undefined}
                                />
                            </Col>
                            <Col>
                                <TextInput
                                    name='ag_signature_last_name'
                                    label='Last Name'
                                    control={control}
                                    required={true}
                                    ariaRequired={true}
                                    readOnly={!!existingAgreement}
                                    maxLength={undefined}
                                    step={undefined}
                                    min={undefined}
                                    max={undefined}
                                />
                            </Col>
                        </Row>

                        <Row className='align-items-end'>
                            <Col>
                                <TextInput
                                    name='ag_signature_date'
                                    label='Date'
                                    control={control}
                                    type='date'
                                    required={true}
                                    ariaRequired={true}
                                    readOnly={!!existingAgreement}
                                    maxLength={undefined}
                                    step={undefined}
                                    min={undefined}
                                    max={undefined}
                                />
                            </Col>
                        </Row>

                        <div className='my-4'>
                            <FormGroup check>
                                <input
                                    type='checkbox'
                                    className='form-check-input'
                                    id='subscribe_to_blog'
                                    {...register('subscribe_to_blog')}
                                />
                                <Label check htmlFor='subscribe_to_blog'>
                                    <strong>Subscribe me to Help Hope Live's blog!</strong>
                                    <br />
                                    <span style={{ fontWeight: 'normal' }}>
                                        Receive an email when we post new fundraising tips, interviews, announcements, events, and more.
                                    </span>
                                </Label>
                            </FormGroup>
                        </div>

                        <div
                            className='p-3 mb-0 mt-4'
                            style={{
                                backgroundColor: '#FEF3CD',
                                borderRadius: '4px',
                            }}
                        >
                            <p className='mb-0' style={{ fontSize: '14px' }}>
                                Once this form is saved, it can no longer be edited.
                                Please make sure all information is correct and you have reviewed all terms before saving.
                            </p>
                        </div>
                    </Form>
                </Col>
            </Row>
            </div>
            <div
                className='sticky-bottom p-4 bg-white mb-0'
                style={{
                    borderTop: '1px solid #0D0106',
                    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.25)',
                    marginBottom: 0,
                }}
            >
                <Button
                    className='mb-0'
                    color='outline-dark'
                    disabled={isSubmitting}
                    onClick={() => history.push('/patient/edit')}
                    style={{ marginBottom: 0 }}
                >
                    Cancel
                </Button>
                <Button
                    className='mb-0'
                    color='primary'
                    onClick={() => handleSubmit(handleNext)()}
                    disabled={saving || disableNext || !isValid}
                    style={{ marginBottom: 0 }}
                >
                    {saving ? `Saving...` : `Save`}
                </Button>
            </div>
        </div>
    );
};
